// Cross-feature semantic stat tile — the KPI card used by Competitive Intelligence
// and Event Intelligence. Distinct from `KpiCard` (Ads/GA4): where KpiCard shows one
// value + a period-over-period DeltaBadge + a sparkline, MetricCardShell renders an
// arbitrary `children` body (a value, a mini bar/gauge, a breakdown) and colors the
// stat by a SEMANTIC `tone` (emerald=good / amber=warning / red=bad) the caller
// computes from the value. The two designs are intentionally separate — a sparkline
// makes no sense for "Accommodation Match 72%" and a red/green tone makes none for
// "Total Spend". This module is the single source for the CI/EI variant.
//
// Was CI's competitive-intelligence/MetricCard.js with a near-identical copy inlined
// in EventIntelligencePage.js. Consolidated here on CI's styling (shared ErrorState +
// SkeletonMetricCard, /25 watermark, p-4). ICON_PATHS is the UNION of both features'
// icon sets so neither loses an icon (CI: target/trendingUp/users/bell/dollar;
// EI: calendar/clock/checklist/share — `target` overlapped and was identical).

import React from "react";
import InfoTip from "./InfoTip";
import { ErrorState } from "./StateMessage";
import { SkeletonMetricCard } from "./Skeleton";

export const TONE_STYLES = {
  emerald: {
    borderTop: "border-t-emerald-500",
    watermark: "text-emerald-500/25 dark:text-emerald-500/15",
    valueClass: "text-emerald-600 dark:text-emerald-400",
    barFill: "bg-emerald-500",
    bg: "bg-emerald-500/[0.06] dark:bg-emerald-500/[0.08]",
    ring: "ring-1 ring-emerald-500/20 dark:ring-emerald-500/20",
  },
  amber: {
    borderTop: "border-t-amber-500",
    watermark: "text-amber-500/25 dark:text-amber-500/15",
    valueClass: "text-amber-600 dark:text-amber-400",
    barFill: "bg-amber-500",
    bg: "bg-amber-500/[0.06] dark:bg-amber-500/[0.08]",
    ring: "ring-1 ring-amber-500/20 dark:ring-amber-500/20",
  },
  red: {
    borderTop: "border-t-red-500",
    watermark: "text-red-500/25 dark:text-red-500/15",
    valueClass: "text-red-600 dark:text-red-400",
    barFill: "bg-red-500",
    bg: "bg-red-500/[0.06] dark:bg-red-500/[0.08]",
    ring: "ring-1 ring-red-500/20 dark:ring-red-500/20",
  },
  gray: {
    borderTop: "border-t-gray-300 dark:border-t-gray-700",
    watermark: "text-gray-400/20 dark:text-gray-500/15",
    valueClass: "text-gray-400 dark:text-gray-500",
    barFill: "bg-gray-400",
  },
  brandBlue: {
    borderTop: "border-t-[#32B1FF]",
    watermark: "text-[#32B1FF]/40 dark:text-[#32B1FF]/25",
    valueClass: "text-[#32B1FF]",
    barFill: "bg-[#32B1FF]",
    bg: "bg-[#32B1FF]/[0.10] dark:bg-[#32B1FF]/[0.14]",
    ring: "ring-1 ring-[#32B1FF]/30 dark:ring-[#32B1FF]/25",
    chipBg: "bg-[#32B1FF]/15 dark:bg-[#32B1FF]/20",
    chipText: "text-[#32B1FF]",
  },
  purple: {
    borderTop: "border-t-purple-500",
    watermark: "text-purple-500/25 dark:text-purple-500/15",
    valueClass: "text-purple-600 dark:text-purple-400",
    barFill: "bg-purple-500",
    bg: "bg-purple-500/[0.06] dark:bg-purple-500/[0.08]",
    ring: "ring-1 ring-purple-500/20 dark:ring-purple-500/20",
    chipBg: "bg-purple-500/15 dark:bg-purple-500/20",
    chipText: "text-purple-600 dark:text-purple-400",
  },
};

const CHIP_FALLBACK = {
  emerald: { chipBg: "bg-emerald-500/15 dark:bg-emerald-500/20", chipText: "text-emerald-600 dark:text-emerald-400" },
  amber:   { chipBg: "bg-amber-500/15 dark:bg-amber-500/20",     chipText: "text-amber-600 dark:text-amber-400" },
  red:     { chipBg: "bg-red-500/15 dark:bg-red-500/20",         chipText: "text-red-600 dark:text-red-400" },
  gray:    { chipBg: "bg-gray-200 dark:bg-gray-800",             chipText: "text-gray-500 dark:text-gray-400" },
};

export const FLAGSHIP_STYLE = {
  borderTop: "border-t-[#32B1FF]",
  watermark: "text-[#32B1FF]/20 dark:text-[#32B1FF]/10",
  bg: "bg-[#32B1FF]/[0.04] dark:bg-[#32B1FF]/[0.08]",
};

// Union of the CI + EI icon sets. `target` was byte-identical in both.
export const ICON_PATHS = {
  target: (
    <>
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </>
  ),
  trendingUp: (
    <>
      <polyline points="3 17 9 11 13 15 21 7" />
      <polyline points="14 7 21 7 21 14" />
    </>
  ),
  users: (
    <>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  bell: (
    <>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </>
  ),
  dollar: (
    <>
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
      <path d="M12 13l1.2 2.6 2.8.4-2 2 .5 2.8L12 19.5 9.5 20.8l.5-2.8-2-2 2.8-.4z" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </>
  ),
  checklist: (
    <>
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </>
  ),
  flag: (
    <>
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <line x1="4" y1="22" x2="4" y2="15" />
    </>
  ),
  eyeOff: (
    <>
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
      <path d="M14.12 14.12a3 3 0 11-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </>
  ),
  eye: (
    <>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  share: (
    <>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </>
  ),
};

export const MetricCardShell = ({ tone = "gray", title, icon = "target", children, footer, flagship = false, tooltip }) => {
  const toneStyle = TONE_STYLES[tone] ?? TONE_STYLES.gray;
  const borderTop = flagship ? FLAGSHIP_STYLE.borderTop : toneStyle.borderTop;
  const watermark = flagship ? FLAGSHIP_STYLE.watermark : toneStyle.watermark;
  const bg = flagship
    ? FLAGSHIP_STYLE.bg
    : (toneStyle.bg ?? "bg-white dark:bg-gray-900");
  const ring = flagship ? "" : (toneStyle.ring ?? "");
  return (
    <div className={`relative ${bg} rounded-xl shadow-sm border dark:border-gray-800 border-t-[3px] ${borderTop} ${ring} p-4 flex flex-col transition-shadow overflow-hidden`}>
      <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
        <div className={`absolute -top-4 -right-4 ${watermark}`}>
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            {ICON_PATHS[icon] ?? ICON_PATHS.target}
          </svg>
        </div>
      </div>
      <div className="relative z-10 text-[13px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400 flex items-center">
        <span>{title}</span>
        {tooltip && <InfoTip text={tooltip} />}
      </div>
      <div className="relative z-10 flex-1 flex flex-col">
        {children}
      </div>
      {footer && <div className="relative z-10 text-[11px] text-gray-500 dark:text-gray-500 mt-2">{footer}</div>}
    </div>
  );
};

// CHIP_FALLBACK is referenced by callers that read chip colors off a tone without
// a chipBg/chipText (emerald/amber/red/gray). Exported for parity with the prior
// CI module surface.
export { CHIP_FALLBACK };

export const MetricSkeleton = SkeletonMetricCard;

export const MetricError = ({ title, message }) => (
  <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-red-200 dark:border-red-900/50 border-t-[3px] border-t-red-500 p-4">
    <div className="text-[13px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400">{title}</div>
    <ErrorState variant="inset" padding="mt-2" message={message} />
  </div>
);
